import { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Circle as CircleIcon,
  Phone as PhoneIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

interface StatusBarProps {
  height: number;
}

type PresenceStatus = 'available' | 'busy' | 'away' | 'offline';

const statusConfig: Record<PresenceStatus, { label: string; color: string }> = {
  available: { label: 'Available', color: '#2e7d32' },
  busy: { label: 'Busy', color: '#d32f2f' },
  away: { label: 'Away', color: '#ed6c02' },
  offline: { label: 'Offline', color: '#9e9e9e' },
};

export function StatusBar({ height }: StatusBarProps) {
  const [status, setStatus] = useState<PresenceStatus>('available');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleStatusChange = (newStatus: PresenceStatus) => {
    setStatus(newStatus);
    setAnchorEl(null);
    // TODO: Call API to update presence status
    // vtwaApi.updatePresence(newStatus);
  };

  const currentStatus = statusConfig[status];

  return (
    <Box
      sx={{
        height,
        minHeight: height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Presence Status */}
      <Button
        onClick={handleStatusClick}
        endIcon={<ExpandMoreIcon />}
        sx={{
          color: 'text.secondary',
          textTransform: 'none',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <CircleIcon
          sx={{
            fontSize: 12,
            mr: 1,
            color: currentStatus.color,
          }}
        />
        {currentStatus.label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {(Object.keys(statusConfig) as PresenceStatus[]).map((key) => (
          <MenuItem
            key={key}
            onClick={() => handleStatusChange(key)}
            selected={status === key}
          >
            <ListItemIcon>
              <CircleIcon
                sx={{
                  fontSize: 12,
                  color: statusConfig[key].color,
                }}
              />
            </ListItemIcon>
            <ListItemText>{statusConfig[key].label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Browser Phone Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          size="small"
          label="Ready"
          color="success"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
        <Tooltip title="Browser Phone">
          <IconButton size="small" color="primary">
            <PhoneIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
